import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { LoaderComponent } from "../../../Common/LoaderComponent";

export const LEDGER_DEPARTMENTS = [
  { id: 2, name: "Jigger" },
  { id: 3, name: "Casting" },
  { id: 4, name: "Finishing" },
  { id: 5, name: "BK" },
  { id: 6, name: "Glazing" },
  { id: 7, name: "GK" },
  { id: 8, name: "Selection" },
  { id: 9, name: "Decoration Local" },
  { id: 10, name: "Decoration Export" },
  { id: 11, name: "DFW" },
  { id: 33, name: "Decoration Firing" },
  { id: 12, name: "Packaging Local" },
  { id: 13, name: "Packaging Export" },
];

// Which dropdowns each department uses (product + shape are always shown)
const GLAZE_DEPARTMENTS = [7, 8, 9, 10, 11, 33, 12, 13];
const DECORATION_DEPARTMENTS = [9, 10, 11, 33, 12, 13];

const MEASURE_LABELS = {
  qty: "Qty",
  export: "Export",
  a: "A Class",
  b: "B Class",
  c: "C Class",
  inner: "Inner",
  master: "Master",
};

const TYPE_LABELS = {
  opening: "Opening Stock",
  transfer_in: "Received",
  transfer_in_dept16: "Received (Dept 16)",
  transfer_out: "Transferred Out",
  production: "Production",
  filter_cake_out: "Filter Cake Used",
  return_to_body: "Returned to Body",
  loss: "Loss",
  calculator_adjustment: "Calculator Difference",
};

const GREEN = "#198754";
const RED = "#dc3545";
const GREEN_BG = "rgba(25,135,84,0.10)";
const RED_BG = "rgba(220,53,69,0.10)";

const HEAD_H = 32; // height of one sticky header row (px)
const NO_SORT = { key: null, dir: "asc" };

// Accepts [..], {data:[..]} or {data:{data:[..]}} (paginated) responses
const toList = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d?.data?.data)) return d.data.data;
  return [];
};

const n = (v) => Number(v || 0);
const fmt = (v) => n(v).toLocaleString(undefined, { maximumFractionDigits: 4 });
const fmtSigned = (v) => (n(v) > 0 ? `+${fmt(v)}` : n(v) < 0 ? `−${fmt(Math.abs(v))}` : "");
const amountStyle = (v) =>
  n(v) > 0
    ? { color: GREEN, background: GREEN_BG, fontWeight: 700, textAlign: "right" }
    : n(v) < 0
      ? { color: RED, background: RED_BG, fontWeight: 700, textAlign: "right" }
      : { textAlign: "right" };

const emptyFilters = { product_id: "", shape_id: "", glaze_id: "", decoration_code: "" };

const labelStyle = { fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 };

/**
 * Self-contained searchable dropdown (no extra dependency).
 * options: [{ value, label }]
 * emptyLabel: if provided, adds a first row that clears the selection (value "")
 */
const SearchableSelect = ({ options, value, onChange, placeholder = "Select", emptyLabel, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
      setQuery("");
    }
  }, [disabled]);

  const selected = options.find((o) => String(o.value) === String(value));
  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => String(o.label).toLowerCase().includes(q)) : options;

  const choose = (v) => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered.length > 0) choose(filtered[0].value);
    }
  };

  const rowStyle = (active) => ({
    padding: "6px 10px",
    fontSize: 13,
    cursor: "pointer",
    background: active ? "#e7f1ff" : "transparent",
  });

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div
        className="form-control"
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          background: disabled ? "#e9ecef" : "#fff",
          overflow: "hidden",
        }}
      >
        <span
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: selected ? "inherit" : "#888",
          }}
        >
          {selected ? selected.label : placeholder}
        </span>
        <span style={{ fontSize: 10, color: "#888", marginLeft: 8 }}>▼</span>
      </div>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1050,
            background: "#fff",
            border: "1px solid #ced4da",
            borderRadius: 4,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            marginTop: 2,
          }}
        >
          <div style={{ padding: 6, borderBottom: "1px solid #eee" }}>
            <input
              ref={inputRef}
              type="text"
              className="form-control form-control-sm"
              placeholder="Search..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
            />
          </div>
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {emptyLabel && !q && (
              <div
                style={{ ...rowStyle(String(value) === ""), color: "#888" }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose("")}
              >
                {emptyLabel}
              </div>
            )}
            {filtered.map((o) => (
              <div
                key={o.value}
                style={rowStyle(String(o.value) === String(value))}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(o.value)}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f3f5")}
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = String(o.value) === String(value) ? "#e7f1ff" : "transparent")
                }
              >
                {o.label}
              </div>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: "8px 10px", fontSize: 13, color: "#888" }}>No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const StockLedgerReport = () => {
  const location = useLocation();
  const [alert, setAlert] = useState(null);
  const [department, setDepartment] = useState(2);
  const [filters, setFilters] = useState(emptyFilters);
  const [dates, setDates] = useState({ from: "", to: "" }); // empty = lifetime
  const [productOptions, setProductOptions] = useState([]);
  const [shapeOptions, setShapeOptions] = useState([]);
  const [glazeOptions, setGlazeOptions] = useState([]);
  const [decorationOptions, setDecorationOptions] = useState([]);
  const [result, setResult] = useState(null); // nothing shown until Search
  const [sort, setSort] = useState(NO_SORT);

  const deptId = Number(department);
  const showGlaze = GLAZE_DEPARTMENTS.includes(deptId);
  const showDecoration = DECORATION_DEPARTMENTS.includes(deptId);
  const departmentName = LEDGER_DEPARTMENTS.find((d) => d.id === deptId)?.name || "";

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 4000);
  };

  useEffect(() => {
    if (!location.state) return;
    showAlert(location.state.message, location.state.type);
  }, [location]);

  useEffect(() => {
    Http.get("/product-list/finished_goods")
      .then((res) => setProductOptions(toList(res)))
      .catch(() => showAlert("Failed to load products", "danger"));
  }, []);

  useEffect(() => {
    if (!showGlaze || glazeOptions.length > 0) return;
    Http.get("/gless-colour")
      .then((res) => setGlazeOptions(toList(res)))
      .catch(() => showAlert("Failed to load glazes", "danger"));
  }, [showGlaze]);

  useEffect(() => {
    if (!showDecoration || decorationOptions.length > 0) return;
    Http.get("/planning-image")
      .then((res) => setDecorationOptions(toList(res)))
      .catch(() => showAlert("Failed to load decorations", "danger"));
  }, [showDecoration]);

  // department change: clear everything, load nothing
  useEffect(() => {
    setFilters(emptyFilters);
    setShapeOptions([]);
    setResult(null);
    setSort(NO_SORT);
  }, [department]);

  const setFilterValue = (name, value) => {
    setFilters((v) => ({ ...v, [name]: value }));
  };

  const onProductChange = (value) => {
    setFilters((v) => ({ ...v, product_id: value, shape_id: "" }));
    setShapeOptions([]);
    if (!value) return;
    Http.get(`/product/${value}`)
      .then((res) => setShapeOptions(res.data?.shapes || []))
      .catch(() => showAlert("Failed to load shapes", "danger"));
  };

  const search = () => {
    if (!filters.product_id) {
      showAlert("Please select a product", "danger");
      return;
    }
    const params = new URLSearchParams({
      department_id: department,
      product_id: filters.product_id,
    });
    if (filters.shape_id) params.append("shape_id", filters.shape_id);
    if (showGlaze && filters.glaze_id) params.append("glaze_id", filters.glaze_id);
    if (showDecoration && filters.decoration_code)
      params.append("decoration_code", filters.decoration_code);
    if (dates.from) params.append("from", dates.from);
    if (dates.to) params.append("to", dates.to);

    setResult(null);
    setSort(NO_SORT);
    Http.get(`/stock-ledger?${params.toString()}`)
      .then((res) => setResult(res.data))
      .catch((err) =>
        showAlert(err?.response?.data?.message || "Failed to load ledger", "danger"),
      );
  };

  const reset = () => {
    setFilters(emptyFilters);
    setShapeOptions([]);
    setDates({ from: "", to: "" });
    setResult(null);
    setSort(NO_SORT);
  };

  // ---------- dropdown options ----------
  const departmentSelectOptions = LEDGER_DEPARTMENTS.map((d) => ({ value: d.id, label: d.name }));
  const productSelectOptions = productOptions.map((p) => ({ value: p.id, label: `${p.code} - ${p.name}` }));
  const shapeSelectOptions = shapeOptions.map((s) => ({ value: s.id, label: s.name }));
  const glazeSelectOptions = glazeOptions.map((g) => ({ value: g.id, label: g.colour_name }));
  const decorationSelectOptions = decorationOptions.map((d) => ({
    value: d.id,
    label: d.decoration_name || d.code || d.name,
  }));

  // ---------- derived ----------
  const measures = result?.measures || [];
  const txns = result?.transactions || [];
  const showSource = txns.some((t) => t.source_group);
  const hasOpeningRow =
    !!dates.from && measures.some((m) => n(result?.opening_balance?.[m]) !== 0);
  const fixedCols = 5 + (showGlaze ? 1 : 0) + (showDecoration ? 1 : 0) + (showSource ? 1 : 0);

  const summary = measures.map((m) => {
    let inn = 0;
    let out = 0;
    txns.forEach((t) => {
      const x = n(t.movement[m]);
      if (x > 0) inn += x;
      else out += x;
    });
    return {
      m,
      opening: n(result?.opening_balance?.[m]),
      inn,
      out,
      closing: n(result?.closing_balance?.[m]),
    };
  });

  // activity breakdown: row count + quantity (absolute) per measure
  const tally = (types) => {
    const list = txns.filter((t) => types.includes(t.type));
    const qty = {};
    measures.forEach((m) => {
      qty[m] = list.reduce((s, t) => s + Math.abs(n(t.movement[m])), 0);
    });
    return { count: list.length, qty };
  };
  const activity = [
    { label: "Received", color: GREEN, ...tally(["transfer_in", "transfer_in_dept16"]) },
    { label: "Transferred Out", color: RED, ...tally(["transfer_out"]) },
    { label: "Production", color: GREEN, ...tally(["production"]) },
    { label: "Loss", color: RED, ...tally(["loss"]) },
  ];

  // ---------- sorting ----------
  const toggleSort = (key) =>
    setSort((s) =>
      s.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : NO_SORT,
    );

  const sortValue = (t, key) => {
    if (key.startsWith("mv:")) return n(t.movement?.[key.slice(3)]);
    if (key.startsWith("bal:")) return n(t.balance?.[key.slice(4)]);
    switch (key) {
      case "date": return t.date || "";
      case "submitted_at": return t.submitted_at || "";
      case "type": return TYPE_LABELS[t.type] || t.type || "";
      case "ref_id": return n(t.ref_id);
      case "shape": return t.shape_name || "";
      case "glaze": return t.glaze_name || "";
      case "decoration": return t.decoration_name || t.decoration_code || "";
      case "source": return n(t.source_group);
      default: return "";
    }
  };

  const rows = sort.key
    ? [...txns].sort((a, b) => {
        const x = sortValue(a, sort.key);
        const y = sortValue(b, sort.key);
        const c =
          typeof x === "number" && typeof y === "number"
            ? x - y
            : String(x).localeCompare(String(y), undefined, { numeric: true });
        return sort.dir === "asc" ? c : -c;
      })
    : txns;

  const downloadCsv = () => {
    if (!result) return;
    const head = [
      "Date",
      "Submitted At",
      "Type",
      "Record #",
      "Shape",
      ...(showGlaze ? ["Glaze"] : []),
      ...(showDecoration ? ["Decoration"] : []),
      ...(showSource ? ["Source Dept"] : []),
      ...measures.flatMap((m) => [`${MEASURE_LABELS[m] || m} Change`, `${MEASURE_LABELS[m] || m} Balance`]),
    ];
    const pad = [
      ...(showGlaze ? [""] : []),
      ...(showDecoration ? [""] : []),
      ...(showSource ? [""] : []),
    ];
    const lines = [head];
    if (hasOpeningRow)
      lines.push(["", "", "Opening Balance", "", "", ...pad, ...measures.flatMap((m) => ["", n(result.opening_balance[m])])]);
    txns.forEach((t) =>
      lines.push([
        t.date || "",
        t.submitted_at || "",
        TYPE_LABELS[t.type] || t.type,
        t.ref_id || "",
        t.shape_name || "",
        ...(showGlaze ? [t.glaze_name || ""] : []),
        ...(showDecoration ? [t.decoration_name || t.decoration_code || ""] : []),
        ...(showSource ? [t.source_group || ""] : []),
        ...measures.flatMap((m) => [n(t.movement[m]), n(t.balance[m])]),
      ]),
    );
    lines.push(["", "", "Closing Balance", "", "", ...pad, ...measures.flatMap((m) => ["", n(result.closing_balance[m])])]);

    const csv = lines
      .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `stock-ledger-${departmentName}-${result.product?.code || filters.product_id}.csv`;
    a.click();
  };

  const v = result?.verification;
  const cell = { padding: "6px 10px", fontSize: 13, verticalAlign: "middle", whiteSpace: "nowrap" };
  const head = { ...cell, background: "#f1f3f5", fontWeight: 700, textTransform: "uppercase", fontSize: 11, textAlign: "center" };

  // sticky header cell for the ledger table (row 1 sticks at top 0, row 2 right below it)
  const stickyHead = (row = 1) => ({
    ...head,
    position: "sticky",
    top: row === 1 ? 0 : HEAD_H,
    zIndex: 3,
    height: HEAD_H,
    padding: "0 10px",
    lineHeight: `${HEAD_H}px`,
    boxSizing: "border-box",
    boxShadow: "inset 0 -1px 0 #dee2e6, inset -1px 0 0 #dee2e6",
    cursor: "pointer",
    userSelect: "none",
  });
  const footCell = {
    ...cell,
    position: "sticky",
    bottom: 0,
    zIndex: 2,
    background: "#f1f3f5",
    boxShadow: "inset 0 1px 0 #dee2e6",
  };

  const sortIcon = (key) => (
    <span style={{ marginLeft: 4, fontSize: 9, opacity: sort.key === key ? 1 : 0.3 }}>
      {sort.key !== key ? "↕" : sort.dir === "asc" ? "▲" : "▼"}
    </span>
  );

  // one sortable header cell
  const sortTh = (key, label, { row = 1, rowSpan, title } = {}) => (
    <th style={stickyHead(row)} rowSpan={rowSpan} title={title} onClick={() => toggleSort(key)}>
      {label}
      {sortIcon(key)}
    </th>
  );

  return (
    <div>
      <IsLogin></IsLogin>
      <LoaderComponent />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Stock Ledger</h5>
        </div>
        <div className="am-pagebody">
          <Alert alert={alert} />

          {/* ---------------- filters ---------------- */}
          <div className="card pd-20 mb-3">
            <div className="row">
              <div className="col-md-3 mb-2">
                <div style={labelStyle}>DEPARTMENT</div>
                <SearchableSelect
                  options={departmentSelectOptions}
                  value={department}
                  onChange={(val) => setDepartment(val)}
                  placeholder="Select department"
                />
              </div>
              <div className="col-md-5 mb-2">
                <div style={labelStyle}>PRODUCT *</div>
                <SearchableSelect
                  options={productSelectOptions}
                  value={filters.product_id}
                  onChange={onProductChange}
                  placeholder="Select product"
                  emptyLabel="Select product"
                />
              </div>
              <div className="col-md-4 mb-2">
                <div style={labelStyle}>SHAPE</div>
                <SearchableSelect
                  options={shapeSelectOptions}
                  value={filters.shape_id}
                  onChange={(val) => setFilterValue("shape_id", val)}
                  disabled={!filters.product_id}
                  placeholder={filters.product_id ? "All shapes" : "Select a product first"}
                  emptyLabel="All shapes"
                />
              </div>
              {showGlaze && (
                <div className="col-md-4 mb-2">
                  <div style={labelStyle}>GLAZE</div>
                  <SearchableSelect
                    options={glazeSelectOptions}
                    value={filters.glaze_id}
                    onChange={(val) => setFilterValue("glaze_id", val)}
                    placeholder="All glazes"
                    emptyLabel="All glazes"
                  />
                </div>
              )}
              {showDecoration && (
                <div className="col-md-4 mb-2">
                  <div style={labelStyle}>DECORATION</div>
                  <SearchableSelect
                    options={decorationSelectOptions}
                    value={filters.decoration_code}
                    onChange={(val) => setFilterValue("decoration_code", val)}
                    placeholder="All decorations"
                    emptyLabel="All decorations"
                  />
                </div>
              )}
              <div className="col-md-2 mb-2">
                <div style={labelStyle}>FROM</div>
                <input type="date" className="form-control" value={dates.from} onChange={(e) => setDates((d) => ({ ...d, from: e.target.value }))} />
              </div>
              <div className="col-md-2 mb-2">
                <div style={labelStyle}>TO</div>
                <input type="date" className="form-control" value={dates.to} onChange={(e) => setDates((d) => ({ ...d, to: e.target.value }))} />
              </div>
            </div>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <button type="button" className="btn btn-primary" onClick={search}>Search</button>
              <button type="button" className="btn btn-outline-secondary" onClick={reset}>Reset</button>
              <button type="button" className="btn btn-outline-success" onClick={downloadCsv} disabled={!result}>Export CSV</button>
              <small className="text-muted ml-2">Leave both dates empty for the full lifetime ledger.</small>
            </div>
          </div>

          {/* ---------------- empty state ---------------- */}
          {!result && (
            <div className="card pd-40 text-center text-muted">
              Select a product and click <b>Search</b> to see its ledger.
            </div>
          )}

          {/* ---------------- result ---------------- */}
          {result && (
            <div className="card pd-20">
              {/* row hover: darken the whole row, keeps the green/red tint */}
              <style>{`.sl-row:hover td { box-shadow: inset 0 0 0 9999px rgba(0,0,0,0.08); }`}</style>

              <div className="mb-3">
                <h5 style={{ marginBottom: 2 }}>
                  {result.product?.code} - {result.product?.name}
                </h5>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  {departmentName} ·{" "}
                  {dates.from || dates.to
                    ? `${dates.from || "Beginning"} → ${dates.to || "Today"}`
                    : "Lifetime"}
                </div>
              </div>

              {v?.checked && (
                <div
                  className="mb-3"
                  style={{
                    padding: "8px 12px",
                    borderRadius: 4,
                    fontSize: 13,
                    background: v.matched ? GREEN_BG : "rgba(255,193,7,0.2)",
                    border: `1px solid ${v.matched ? GREEN : "#ffc107"}`,
                  }}
                >
                  {v.matched
                    ? "✔ Closing balance matches the stock report."
                    : "⚠ The highlighted “Calculator Difference” row is stock the transactions don’t explain. The closing balance is forced to equal the stock report."}
                </div>
              )}
              {v && !v.checked && (
                <div className="mb-3 text-muted" style={{ fontSize: 13 }}>
                  An end date is set, so the balance isn’t compared with the current stock report.
                </div>
              )}

              {/* summary */}
              <div className="d-flex flex-wrap align-items-start mb-4" style={{ gap: 24 }}>
                <div className="table-responsive" style={{ width: "auto", maxWidth: "100%" }}>
                  <table className="table table-sm table-bordered mb-0" style={{ minWidth: 480 }}>
                    <thead>
                      <tr>
                        <th style={{ ...head, textAlign: "left" }}>Summary</th>
                        <th style={head}>Opening Balance</th>
                        <th style={{ ...head, color: GREEN }}>Total In</th>
                        <th style={{ ...head, color: RED }}>Total Out</th>
                        <th style={head}>Closing Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.map((s) => (
                        <tr key={s.m}>
                          <td style={{ ...cell, fontWeight: 700 }}>{MEASURE_LABELS[s.m] || s.m}</td>
                          <td style={{ ...cell, textAlign: "right" }}>{fmt(s.opening)}</td>
                          <td style={{ ...cell, textAlign: "right", color: GREEN, fontWeight: 700 }}>{s.inn ? `+${fmt(s.inn)}` : "—"}</td>
                          <td style={{ ...cell, textAlign: "right", color: RED, fontWeight: 700 }}>{s.out ? `−${fmt(Math.abs(s.out))}` : "—"}</td>
                          <td style={{ ...cell, textAlign: "right", fontWeight: 700 }}>{fmt(s.closing)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* activity: rows + qty per type */}
                <div className="table-responsive" style={{ width: "auto", maxWidth: "100%" }}>
                  <table className="table table-sm table-bordered mb-0">
                    <thead>
                      <tr>
                        <th style={{ ...head, textAlign: "left" }}>Activity</th>
                        <th style={head}>Rows</th>
                        {measures.map((m) => (
                          <th key={m} style={head}>{MEASURE_LABELS[m] || m} Qty</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ ...cell, fontWeight: 700 }}>Total Rows</td>
                        <td style={{ ...cell, textAlign: "right", fontWeight: 700 }}>{txns.length.toLocaleString()}</td>
                        {measures.map((m) => (
                          <td key={m} style={{ ...cell, textAlign: "right", color: "#aaa" }}>—</td>
                        ))}
                      </tr>
                      {activity.map((a) => (
                        <tr key={a.label}>
                          <td style={{ ...cell, fontWeight: 700 }}>{a.label}</td>
                          <td style={{ ...cell, textAlign: "right" }}>{a.count.toLocaleString()}</td>
                          {measures.map((m) => (
                            <td key={m} style={{ ...cell, textAlign: "right", color: a.qty[m] ? a.color : "#aaa", fontWeight: a.qty[m] ? 700 : 400 }}>
                              {a.qty[m] ? fmt(a.qty[m]) : "—"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ledger: fixed height, sticky header + closing row, sortable columns */}
              <div style={{ height: "60vh", minHeight: 320, overflow: "auto", border: "1px solid #dee2e6" }}>
                <table className="table table-sm table-bordered mb-0">
                  <thead>
                    <tr>
                      {sortTh("date", "Date", { rowSpan: 2 })}
                      {sortTh("submitted_at", "Submitted At", { rowSpan: 2, title: "When the record was entered into the system" })}
                      {sortTh("type", "Type", { rowSpan: 2 })}
                      {sortTh("ref_id", "Record #", { rowSpan: 2, title: "Internal database id of the source record" })}
                      {sortTh("shape", "Shape", { rowSpan: 2 })}
                      {showGlaze && sortTh("glaze", "Glaze", { rowSpan: 2 })}
                      {showDecoration && sortTh("decoration", "Decoration", { rowSpan: 2 })}
                      {showSource && sortTh("source", "Source Dept", { rowSpan: 2 })}
                      {measures.map((m) => (
                        <th key={m} style={{ ...stickyHead(1), cursor: "default" }} colSpan={2}>{MEASURE_LABELS[m] || m}</th>
                      ))}
                    </tr>
                    <tr>
                      {measures.map((m) => (
                        <React.Fragment key={m}>
                          {sortTh(`mv:${m}`, "Change", { row: 2 })}
                          {sortTh(`bal:${m}`, "Balance", { row: 2 })}
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {hasOpeningRow && (
                      <tr style={{ background: "#f8f9fa" }}>
                        <td style={{ ...cell, fontWeight: 700 }} colSpan={fixedCols}>Opening Balance</td>
                        {measures.map((m) => (
                          <React.Fragment key={m}>
                            <td style={cell}></td>
                            <td style={{ ...cell, textAlign: "right", fontWeight: 700 }}>{fmt(result.opening_balance[m])}</td>
                          </React.Fragment>
                        ))}
                      </tr>
                    )}

                    {rows.length === 0 && (
                      <tr>
                        <td style={{ ...cell, textAlign: "center" }} colSpan={fixedCols + measures.length * 2}>
                          No transactions found
                        </td>
                      </tr>
                    )}

                    {rows.map((t, i) => (
                      <tr
                        key={i}
                        className="sl-row"
                        style={t.type === "calculator_adjustment" ? { background: "rgba(255,193,7,0.2)" } : undefined}
                      >
                        <td style={cell}>{t.date ? t.date.slice(0, 10) : "—"}</td>
                        <td style={cell}>{t.submitted_at ? String(t.submitted_at).replace("T", " ").slice(0, 16) : "—"}</td>
                        <td style={{ ...cell, fontWeight: 600 }}>{TYPE_LABELS[t.type] || t.type}</td>
                        <td style={{ ...cell, color: "#888", textAlign: "center" }}>{t.ref_id ? `#${t.ref_id}` : ""}</td>
                        <td style={cell}>{t.shape_name || ""}</td>
                        {showGlaze && <td style={cell}>{t.glaze_name || ""}</td>}
                        {showDecoration && <td style={cell}>{t.decoration_name || t.decoration_code || ""}</td>}
                        {showSource && <td style={{ ...cell, textAlign: "center" }}>{t.source_group || ""}</td>}
                        {measures.map((m) => (
                          <React.Fragment key={m}>
                            <td style={{ ...cell, ...amountStyle(t.movement[m]) }}>{fmtSigned(t.movement[m])}</td>
                            <td style={{ ...cell, textAlign: "right" }}>{fmt(t.balance[m])}</td>
                          </React.Fragment>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={{ ...footCell, fontWeight: 700 }} colSpan={fixedCols}>Closing Balance</td>
                      {measures.map((m) => (
                        <React.Fragment key={m}>
                          <td style={footCell}></td>
                          <td style={{ ...footCell, textAlign: "right", fontWeight: 700 }}>{fmt(result.closing_balance[m])}</td>
                        </React.Fragment>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockLedgerReport;